import * as React from 'react';

import { ContentCard } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { useBrokeredLoadFiles } from '../hooks/useBrokeredLoadFiles';

import { $remoteBrokeredLoadDetail } from '~/modules/brokerage/_state/brokeredLoadDetail.state';
import { useTranslations } from '~/modules/brokerage/i18n/useTranslations';
import { BrokeredLoadRecord } from '~/modules/brokerage/types/brokeredLoad';
import { FilesTab } from '~/shared/components/files/FilesTab';
import { GenericFile } from '~/shared/types/file';

type Props = {
  load: BrokeredLoadRecord;
};

const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
];

export const BrokeredLoadTabDocuments: React.FC<Props> = ({ load }) => {
  const t = useTranslations();

  const fileMutations = useBrokeredLoadFiles();
  const { refetch } = useAtomValue($remoteBrokeredLoadDetail);

  const handleAllUploadsComplete = React.useCallback(() => {
    refetch();
  }, [refetch]);

  const documentFiles = React.useMemo<GenericFile[]>(() => {
    if (!load.files) return [];

    return load.files
      .filter(
        (file) =>
          !file.mimeType.startsWith('image/') ||
          DOCUMENT_MIME_TYPES.includes(file.mimeType),
      )
      .map((file) => ({
        id: file.id,
        fileName: file.fileName,
        fileSize: file.fileSize,
        mimeType: file.mimeType,
        status: file.status,
        entityType: file.entityType,
        entityId: file.entityId,
        metadata: file.metadata,
        createdAt: file.createdAt?.toString(),
        updatedAt: file.updatedAt?.toString(),
      }));
  }, [load.files]);

  const dropZoneText = React.useMemo(
    () => ({
      primary: (
        <>
          Click <span className="font-semibold text-primary">here</span> or drag
          documents to upload
        </>
      ),
      secondary: 'Supports PDF, Word, Excel, and other document formats',
    }),
    [],
  );

  const acceptMimeTypes = DOCUMENT_MIME_TYPES.join(',');

  return (
    <div className="@BrokeredLoadTabDocuments flex max-h-full flex-col gap-5 p-5">
      <ContentCard>
        <FilesTab
          entityId={load.id}
          files={documentFiles}
          mutations={fileMutations}
          onAllUploadsComplete={handleAllUploadsComplete}
          mimeType={acceptMimeTypes}
          dropZoneTheme="compact"
          dropZoneText={dropZoneText}
        />
        {documentFiles.length === 0 && (
          <div className="p-4 text-center text-neutral/60">
            {t.brokerage.loads.detail.documents.empty}
          </div>
        )}
      </ContentCard>
    </div>
  );
};
